import { getStorage, isArray, isBrowser, isIP, isValidUrl, pickBy } from '@sortlist-frontend/utils';

import { config } from '../defaults';
import {
  FEATURE_FLAGS_OVERRIDE_ASSIGNATOR,
  FEATURE_FLAGS_OVERRIDE_PARAM,
  FEATURE_FLAGS_OVERRIDE_SEPARATOR,
} from './constants';
import { EnabledForConfig, Environment, FeatureFlagOverride } from './types';
import { isValueActive } from './utils';

export type ConfigOverride = ReturnType<typeof getConfigOverrides>;

export function getConfigOverrides(_url: string) {
  if (!isValidUrl(_url))
    return {
      configOverrideFromEnvironment: {},
      configOverrideFromLocalStorage: {},
      configOverrideFromParams: {},
    };

  const url = new URL(_url);
  const { hostname, searchParams } = url;
  const featureFlagsParams = searchParams.get(FEATURE_FLAGS_OVERRIDE_PARAM) ?? undefined;

  const configOverrideFromParams = extractConfigFromParams(featureFlagsParams);
  const configOverrideFromLocalStorage = isBrowser() ? extractConfigFromLocalStorage() : {};
  const configOverrideFromEnvironment = determineConfigFromEnvironment(hostname);

  return {
    configOverrideFromEnvironment,
    configOverrideFromLocalStorage,
    configOverrideFromParams,
  };
}

export function extractConfigFromParams(params?: string): Partial<FeatureFlagOverride> {
  if (params == null) {
    return {};
  }

  return stringToConfigObject(params);
}

export function extractConfigFromLocalStorage(): Partial<FeatureFlagOverride> {
  if (!isBrowser()) {
    return {};
  }
  const value = getStorage(FEATURE_FLAGS_OVERRIDE_PARAM);

  if (typeof value !== 'string') {
    return {};
  }

  return stringToConfigObject(value);
}

function stringToConfigObject(value: string) {
  return value.split(FEATURE_FLAGS_OVERRIDE_SEPARATOR).reduce((memo, config: string) => {
    // [0] is the feature flag name
    // [1] is the defaultValue value
    const chunks = config.split(FEATURE_FLAGS_OVERRIDE_ASSIGNATOR);
    const key = chunks[0];
    const rawValue = chunks[1];
    const value = rawValue === 'true' ? true : rawValue === 'false' ? false : rawValue;

    return {
      ...memo,
      [key]: {
        defaultValue: value,
      },
    };
  }, {});
}

export function determineConfigFromEnvironment(hostname: string): Partial<FeatureFlagOverride> {
  const environment = getEnvironmentFromHostname(hostname);

  if (environment == null) {
    return {};
  }

  const environments = isArray(environment) ? environment : [environment];
  const flagsWithEnvironmentConfig = pickBy(config, (value) => 'enabledForEnvironments' in value);

  return Object.entries(flagsWithEnvironmentConfig).reduce((memo, [key, value]) => {
    if ((value as EnabledForConfig).enabledForEnvironments?.some((enabledEnv) => environments.includes(enabledEnv))) {
      const currentValue = value.defaultValue;
      return {
        ...memo,
        [key]: {
          defaultValue: isValueActive(currentValue) ? currentValue : true,
        },
      };
    }

    return memo;
  }, {});
}

function getEnvironmentFromHostname(hostname: string): Environment | Environment[] | undefined {
  if (hostname === 'localhost') {
    return 'development';
  }

  if (hostname.endsWith('sandbox.sortlist.cloud')) {
    const sandboxId = hostname.match(/\d+/)?.[0];
    if (sandboxId == null) {
      return undefined;
    }
    return [`sandbox-${Number(sandboxId)}`, 'sandbox'];
  }

  if (hostname.includes('sortlist-test.')) {
    return 'staging';
  }

  if (hostname.includes('sortlist.')) {
    // This shouldn't log an error, but for production we skip this check, and instead
    // should enable the flag by default
    return 'production';
  }

  // if it's an IP address, return production (as we're probably getting the request from an internal IP)
  if (isIP(hostname) !== 0 && process.env.NODE_ENV === 'production' && hostname.startsWith('10.0.')) {
    return 'production';
  }

  return undefined;
}
